import React, { useState } from 'react';
import { sanitizedInnerHtml } from 'src/utils/sanitize';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import AltRouteRoundedIcon from '@mui/icons-material/AltRouteRounded';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
//Styles
import styles from './Preview.module.css';
import {
  FORM_TYPES,
  isHalfWidthFormType
} from 'src/components/form-builder/CreateQuestionnaire/constants/formTypes';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField
} from '@mui/material';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import SignatureCanvas from 'react-signature-canvas';
import EmbedContentModal from '../Popup/types/formTypes/EmbedContent/EmbedContentModal';

function PreviewForm({ data, connected, subForm }) {
  let specificCondition = data?.condition;
  const [openModal, setOpenModal] = useState(false);
  const t = useAutoTranslation();

  // Shared label markup so every field type renders consistently.
  const renderLabel = (element) => (
    <div className={styles.fieldLabel}>
      {t(element.label)}
      {element.isRequired && <span className={styles.requiredMark}>*</span>}
    </div>
  );

  const conditionValue = Array.isArray(specificCondition?.value)
    ? specificCondition.value.join(', ')
    : specificCondition?.value;

  const renderField = (element, index) => {
    const key = element.id || index;
    switch (element.type) {
      case FORM_TYPES.SHORT_TEXT:
        return (
          <div className={styles.field} key={key}>
            {renderLabel(element)}
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder={t('Short answer')}
            />
          </div>
        );
      case FORM_TYPES.FILE_UPLOAD:
        return (
          <div className={styles.field} key={key}>
            {renderLabel(element)}
            <div className={styles.dropzone}>
              <CloudUploadOutlinedIcon className={styles.dropzoneIcon} />
              <span className={styles.dropzoneText}>
                {t('Drag & drop a file here')}
              </span>
              <span className={styles.dropzoneHint}>
                {t('PDF, image or text file')}
              </span>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                component="label"
                className={styles.dropzoneBtn}
              >
                {t('Upload File')}
                <input accept="image/*,.pdf,.txt" hidden type="file" />
              </Button>
            </div>
          </div>
        );
      case FORM_TYPES.EMAIL_INPUT:
        return (
          <div className={styles.field} key={key}>
            {renderLabel(element)}
            <TextField
              variant="outlined"
              type={'email'}
              size="small"
              fullWidth
              placeholder={t('name@example.com')}
            />
          </div>
        );
      case FORM_TYPES.DATE_TIME_INPUT:
        return (
          <div className={styles.field} key={key}>
            {renderLabel(element)}
            <TextField
              variant="outlined"
              type={'date'}
              size="small"
              fullWidth
            />
          </div>
        );
      case FORM_TYPES.LONG_TEXT:
        return (
          <div className={styles.field} key={key}>
            {renderLabel(element)}
            <TextField
              required={element.isRequired}
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              placeholder={t('Long answer')}
            />
          </div>
        );
      case FORM_TYPES.MULTIPLE_CHOICE:
        return (
          <div className={styles.field} key={key}>
            <FormControl>
              {renderLabel(element)}
              <RadioGroup>
                {element.options.map((option, i) => {
                  return (
                    <FormControlLabel
                      key={i}
                      value={option}
                      control={<Radio size="small" />}
                      label={t(option)}
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>
          </div>
        );
      case FORM_TYPES.DROPDOWN:
        return (
          <div className={styles.field} key={key}>
            <FormControl fullWidth>
              {renderLabel(element)}
              <Select
                variant="outlined"
                size="small"
                defaultValue={element.options[0]}
                fullWidth
              >
                {element.options.map((option) => {
                  return (
                    <MenuItem key={option} value={option}>
                      {t(option)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </div>
        );
      case FORM_TYPES.CHECKBOX_INPUT:
        if (element.isIdentifier && !element.options.includes('Other')) {
          element.options.push('Other');
        }
        return (
          <div className={styles.field} key={key}>
            {renderLabel(element)}
            <FormGroup>
              {element.options.map((option, i) => {
                return (
                  <React.Fragment key={i}>
                    <FormControlLabel
                      control={<Checkbox size="small" />}
                      label={t(option)}
                    />
                    {option === 'Other' && (
                      <TextField
                        disabled
                        size="small"
                        placeholder={t(
                          "Only enabled when 'Other' option is selected"
                        )}
                        variant="outlined"
                        fullWidth
                        sx={{ mt: 1 }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </FormGroup>
          </div>
        );
      case FORM_TYPES.LINEAR_SCALE:
        return (
          <div className={styles.field} key={key}>
            {renderLabel(element)}
            <RadioGroup row className={styles.scaleRow}>
              {element.options.map((option, i) => {
                return (
                  <div key={i} className={styles.scaleItem}>
                    <span>{t(option)}</span>
                    <Radio size="small" value={option} />
                  </div>
                );
              })}
            </RadioGroup>
          </div>
        );

      case FORM_TYPES.EMBED_CONTENT:
        return (
          <div className={styles.field} key={key}>
            <FormLabel>
              <EmbedContentModal
                open={openModal}
                setOpen={setOpenModal}
                text={t(element.value)}
                title={t(element.label)}
              />

              <div
                className={
                  element.hasModalContent
                    ? styles.clickBlock
                    : styles.previewLabel
                }
                onClick={() => element.hasModalContent && setOpenModal(true)}
              >
                <span>{t(element.label)}</span>
                {element.hasModalContent && (
                  <div className={styles.viewMoreSection}>
                    <AdsClickIcon fontSize="10px" />
                    <p className={styles.moreDetails}>
                      {t('Click to view more details')}
                    </p>
                  </div>
                )}
              </div>
            </FormLabel>
            {!element.hasModalContent && (
              <div
                dangerouslySetInnerHTML={sanitizedInnerHtml(t(element.value))}
              />
            )}
          </div>
        );
      case FORM_TYPES.SIGNATURE:
        return (
          <div className={styles.field} key={key}>
            {renderLabel(element)}
            <div className={styles.signaturePad}>
              <SignatureCanvas />
            </div>
          </div>
        );
      default:
        return <React.Fragment key={key}></React.Fragment>;
    }
  };

  const conditionNote = specificCondition?.sectionId ? (
    <div className={styles.conditionNote}>
      <AltRouteRoundedIcon
        fontSize="small"
        className={styles.conditionNoteIcon}
      />
      <span>
        {data.formType ? t('This form') : t('This section')}{' '}
        {t('will be shown only when')}{' '}
        <strong>{specificCondition?.condition}</strong> {t('is')}{' '}
        <strong>{conditionValue}</strong>
      </span>
    </div>
  ) : null;
  const hasChildSections = (data.groupsConnectedTo || []).length > 0;
  const fields = data.formElements.length ? (
    <Box className={styles.fieldsGrid}>
      {data.formElements.map((element, index) => (
        <div
          key={element.id || index}
          className={
            isHalfWidthFormType(element.type)
              ? styles.fieldCellHalf
              : styles.fieldCellFull
          }
        >
          {renderField(element, index)}
        </div>
      ))}
    </Box>
  ) : hasChildSections ? null : (
    <div className={styles.emptyFields}>
      {data.formType
        ? t('No fields have been added to this form yet.')
        : t('No fields have been added to this section yet.')}
    </div>
  );

  if (connected) {
    return (
      <div className={styles.sectionCard}>
        {conditionNote}
        <div className={styles.sectionCardHeader}>
          <h3 className={styles.sectionCardTitle}>{t(data.title)}</h3>
          {data.description && (
            <p className={styles.sectionCardDesc}>{t(data.description)}</p>
          )}
        </div>
        {fields}
      </div>
    );
  }

  return (
    <div>
      {conditionNote}
      <div className={subForm ? styles.subFormHeader : styles.formHeader}>
        <h2 className={subForm ? styles.subFormTitle : styles.formTitle}>
          {t(data.title)}
        </h2>
        {data.description && (
          <p className={styles.formDescription}>{t(data.description)}</p>
        )}
      </div>
      {fields}
    </div>
  );
}

export default PreviewForm;
